<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import BetaPageImageField from '@/components/beta-dashboard/molecules/BetaPageImageField.vue'
import modal from '@/services/modal'
import notification, { type ToastId } from '@/services/notification'
import { useBetaDashboardStore } from '@/stores/beta-dashboard'
import { useMediaManagerStore } from '@/stores/modal/media-manager'
import type { BetaPage } from '@/types/beta-dashboard'
import { isExternalLinkPage } from '@/utils/page'

const props = defineProps<{
    page: BetaPage | null
}>()

const emit = defineEmits<{
    close: []
    saved: [page: BetaPage]
    deleted: [pageId: number]
}>()

const betaStore = useBetaDashboardStore()
const mediaManagerStore = useMediaManagerStore()

const draft = ref<any>(null)
const saving = ref(false)
const cloning = ref(false)
const deleting = ref(false)

const isOpen = computed(() => Boolean(props.page && draft.value))
const isHome = computed(() => Boolean(draft.value?.isHome))
const isExternalLink = computed(() => isExternalLinkPage(draft.value))
const busy = computed(() => saving.value || cloning.value || deleting.value)

const cropOptions = [
    { value: 'center-center', label: 'Centralizado' },
    { value: 'top-right', label: 'Topo à direita' },
    { value: 'top-center', label: 'Topo ao centro' },
    { value: 'top-left', label: 'Topo à esquerda' },
    { value: 'center-right', label: 'Centro à direita' },
    { value: 'center-left', label: 'Centro à esquerda' },
    { value: 'bottom-right', label: 'Inferior à direita' },
    { value: 'bottom-center', label: 'Inferior ao centro' },
    { value: 'bottom-left', label: 'Inferior à esquerda' },
]

watch(
    () => props.page,
    (page) => {
        if (!page) {
            draft.value = null
            return
        }

        const copy = JSON.parse(JSON.stringify(page))
        copy.visible = page.visible == 1
        copy.menu_visible = page.menu_visible == 1
        copy.show_opportunities_button = page.show_opportunities_button == 1
        copy.mainImage = {
            ...(copy.mainImage ?? {}),
            url: copy.mainImage?.url ?? null,
            class: copy.mainImage?.class ?? 'center-center',
        }

        if (isExternalLinkPage(copy)) copy.visible = false
        draft.value = copy
    },
    { immediate: true },
)

const close = () => {
    if (busy.value || mediaManagerStore.isOpenModal) return
    emit('close')
}

const selectImage = (field: 'mainImage' | 'ogImage') => {
    if (busy.value) return

    mediaManagerStore.setMaxSelectedMedias(1)
    mediaManagerStore.setAction((media: any) => {
        if (!media?.content || !draft.value) return

        if (field === 'mainImage') {
            draft.value.mainImage.url = media.content
        } else {
            draft.value.ogImage = media.content
        }
    })
    mediaManagerStore.setIsOpenModal(true)
}

const save = async () => {
    if (busy.value || !draft.value) return

    const name = String(draft.value.name || '').trim()
    if (!name) {
        notification.simple('Preencha o nome da página.', 'warning')
        return
    }

    saving.value = true
    const toastId: ToastId = notification.loading('Atualizando página…')

    try {
        const payload = JSON.parse(JSON.stringify({
            ...draft.value,
            name,
            visible: isExternalLink.value ? false : draft.value.visible,
        })) as BetaPage

        await betaStore.updatePage(payload)
        notification.update(toastId, 'Página atualizada com sucesso.', 'success')
        emit('saved', payload)
        emit('close')
    } catch (error) {
        notification.update(
            toastId,
            error instanceof Error ? error.message : 'Erro ao atualizar página.',
            'error',
        )
    } finally {
        saving.value = false
    }
}

const clonePage = async () => {
    if (busy.value || !draft.value || isHome.value) return

    const confirmed = await modal.confirmation(
        'Clonar página?',
        `Uma cópia de “${draft.value.name}” será criada.`,
        {
            type: 'info',
            textConfirm: 'Sim, clonar',
            textCancel: 'Cancelar',
        },
    )
    if (!confirmed) return

    cloning.value = true
    const toastId: ToastId = notification.loading('Clonando página…')

    try {
        await betaStore.duplicatePage(Number(draft.value.id))
        notification.update(toastId, 'Página clonada com sucesso.', 'success')
        emit('close')
    } catch (error) {
        notification.update(
            toastId,
            error instanceof Error ? error.message : 'Erro ao clonar página.',
            'error',
        )
    } finally {
        cloning.value = false
    }
}

const deletePage = async () => {
    if (busy.value || !draft.value || isHome.value) return

    const pageId = Number(draft.value.id)
    const confirmed = await modal.confirmation(
        'Excluir página?',
        `“${draft.value.name}” será removida permanentemente.`,
        {
            type: 'danger',
            textConfirm: 'Sim, excluir',
            textCancel: 'Cancelar',
        },
    )
    if (!confirmed) return

    deleting.value = true
    const toastId: ToastId = notification.loading('Excluindo página…')

    try {
        await betaStore.deletePage(pageId)
        notification.update(toastId, 'Página excluída com sucesso.', 'success')
        emit('deleted', pageId)
        emit('close')
    } catch (error) {
        notification.update(
            toastId,
            error instanceof Error ? error.message : 'Erro ao excluir página.',
            'error',
        )
    } finally {
        deleting.value = false
    }
}
</script>

<template>
    <Teleport to="body">
        <div
            v-if="isOpen"
            class="beta-page-settings-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="beta-page-settings-title"
            @click.self="close"
        >
            <form class="beta-page-settings-dialog__panel" @submit.prevent="save">
                <header class="beta-page-settings-dialog__header">
                    <div>
                        <p>Configurações da página</p>
                        <h2 id="beta-page-settings-title">{{ draft.name }}</h2>
                    </div>
                    <div class="beta-page-settings-dialog__header-actions">
                        <button
                            v-if="!isHome"
                            type="button"
                            class="is-secondary"
                            :disabled="busy"
                            @click="clonePage"
                        >
                            {{ cloning ? 'Clonando…' : 'Clonar' }}
                        </button>
                        <button
                            v-if="!isHome"
                            type="button"
                            class="is-danger"
                            :disabled="busy"
                            @click="deletePage"
                        >
                            {{ deleting ? 'Excluindo…' : 'Excluir' }}
                        </button>
                        <button type="button" class="is-close" aria-label="Fechar" :disabled="busy" @click="close">
                            ×
                        </button>
                    </div>
                </header>

                <div class="beta-page-settings-dialog__body">
                    <section class="beta-page-settings-dialog__section">

                        <label class="beta-page-settings-dialog__field">
                            <span>Nome da página</span>
                            <input v-model="draft.name" type="text" maxlength="80">
                        </label>

                        <div class="beta-page-settings-dialog__toggles">
                            <label
                                :class="isExternalLink && 'is-disabled'"
                                :title="isExternalLink ? 'Indisponível para links externos' : undefined"
                            >
                                <input v-model="draft.visible" type="checkbox" :disabled="isExternalLink">
                                <strong>Exibir na página inicial</strong>
                            </label>
                            <label>
                                <input v-model="draft.menu_visible" type="checkbox">
                                <strong>Exibir no menu</strong>
                            </label>
                            <label>
                                <input v-model="draft.show_opportunities_button" type="checkbox">
                                <strong>Botão flutuante</strong>
                            </label>
                        </div>
                    </section>

                    <section class="beta-page-settings-dialog__section">
                        <div class="beta-page-settings-dialog__section-heading">
                            <div>
                                <h3>Imagens</h3>
                            </div>
                        </div>

                        <div class="beta-page-settings-dialog__images">
                            <BetaPageImageField
                                label="Capa da página"
                                description="Imagem principal dos cards e listagens"
                                :image="draft.mainImage.url"
                                @select="selectImage('mainImage')"
                                @remove="draft.mainImage.url = null"
                            />
                            <BetaPageImageField
                                label="Prévia de compartilhamento"
                                description="Imagem exibida no WhatsApp e redes sociais"
                                :image="draft.ogImage"
                                @select="selectImage('ogImage')"
                                @remove="draft.ogImage = null"
                            />
                        </div>

                        <label class="beta-page-settings-dialog__field">
                            <span>Enquadramento da capa</span>
                            <select v-model="draft.mainImage.class">
                                <option v-for="option in cropOptions" :key="option.value" :value="option.value">
                                    {{ option.label }}
                                </option>
                            </select>
                        </label>
                    </section>

                    <section v-if="isHome" class="beta-page-settings-dialog__section">
                        <div class="beta-page-settings-dialog__section-heading">
                            <div>
                                <h3>Home</h3>
                            </div>
                        </div>
                        <div class="beta-page-settings-dialog__columns">
                            <label class="beta-page-settings-dialog__field">
                                <span>Máximo de posts exibidos</span>
                                <input v-model.number="draft.pages" type="number" min="0">
                            </label>
                            <label class="beta-page-settings-dialog__field">
                                <span>Texto do H1</span>
                                <input v-model="draft.seoText" type="text" maxlength="255">
                            </label>
                        </div>
                    </section>

                    <section class="beta-page-settings-dialog__section">
                        <div class="beta-page-settings-dialog__section-heading">
                            <div>
                                <h3>Códigos avançados</h3>
                            </div>
                        </div>
                        <div class="beta-page-settings-dialog__advanced-fields">
                            <label class="beta-page-settings-dialog__field">
                                <span>Código dentro da tag &lt;head&gt;</span>
                                <textarea v-model="draft.customHeader" rows="3" spellcheck="false"></textarea>
                            </label>
                            <label class="beta-page-settings-dialog__field">
                                <span>Código dentro da tag &lt;body&gt;</span>
                                <textarea v-model="draft.customFooter" rows="3" spellcheck="false"></textarea>
                            </label>
                        </div>
                    </section>
                </div>

                <footer class="beta-page-settings-dialog__footer">
                    <button type="button" class="is-cancel" :disabled="busy" @click="close">Cancelar</button>
                    <button type="submit" class="is-save" :disabled="busy">
                        {{ saving ? 'Salvando…' : 'Salvar configurações' }}
                    </button>
                </footer>
            </form>
        </div>
    </Teleport>
</template>

<style scoped>
.beta-page-settings-dialog {
    position: fixed;
    inset: 0;
    z-index: 100;
    display: grid;
    place-items: center;
    overflow-y: auto;
    background: rgba(13, 29, 51, 0.6);
    padding: 16px;
    backdrop-filter: blur(8px);
}

.beta-page-settings-dialog__panel {
    display: grid;
    width: min(820px, 100%);
    height: min(720px, calc(100vh - 32px));
    grid-template-rows: auto minmax(0, 1fr) auto;
    overflow: hidden;
    border: 1px solid rgba(255, 255, 255, 0.68);
    border-radius: 22px;
    background: #fff;
}

.beta-page-settings-dialog__header,
.beta-page-settings-dialog__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 14px 18px;
}

.beta-page-settings-dialog__header {
    border-bottom: 1px solid #e8edf3;
}

.beta-page-settings-dialog__header p {
    color: var(--main-color);
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.16em;
    text-transform: uppercase;
}

.beta-page-settings-dialog__header h2 {
    margin-top: 2px;
    color: #172d4a;
    font-size: 20px;
    font-weight: 700;
    letter-spacing: -0.03em;
}

.beta-page-settings-dialog__header > div:first-child > span {
    display: block;
    margin-top: 4px;
    color: #7f8da1;
    font-size: 9px;
}

.beta-page-settings-dialog__header-actions {
    display: flex;
    align-items: center;
    gap: 6px;
}

.beta-page-settings-dialog__header-actions button {
    min-height: 34px;
    border-radius: var(--beta-button-radius);
    padding: 0 10px;
    font-size: 12px;
    font-weight: 700;
}

.beta-page-settings-dialog__header-actions .is-secondary {
    background: #edf2f7;
    color: #536780;
}

.beta-page-settings-dialog__header-actions .is-danger {
    background: #fff0f0;
    color: #b64e4e;
}

.beta-page-settings-dialog__header-actions .is-close {
    width: 34px;
    padding: 0;
    background: #f0f4f9;
    color: #60718a;
    font-size: 18px;
}

.beta-page-settings-dialog__body {
    display: grid;
    min-height: 0;
    align-content: start;
    gap: 10px;
    overflow-y: auto;
    overscroll-behavior: contain;
    scrollbar-gutter: stable;
    padding: 12px 16px 14px;
}

.beta-page-settings-dialog__section {
    border: 1px solid #e6ebf2;
    border-radius: 16px;
    background: #f9fbfd;
    padding: 12px;
}

.beta-page-settings-dialog__section-heading h3 {
    color: #263d5a;
    font-size: 12px;
    font-weight: 700;
}

.beta-page-settings-dialog__section-heading p {
    display: block;
    margin-top: 3px;
    color: #8190a4;
    font-size: 8px;
}

.beta-page-settings-dialog__field {
    display: grid;
    gap: 5px;
    margin-top: 10px;
}

.beta-page-settings-dialog__section > .beta-page-settings-dialog__field:first-child {
    margin-top: 0;
}

.beta-page-settings-dialog__field > span {
    color: #3a506d;
    font-size: 12px;
    font-weight: 700;
}

.beta-page-settings-dialog__field input,
.beta-page-settings-dialog__field select,
.beta-page-settings-dialog__field textarea {
    width: 100%;
    border: 1px solid #dce4ef;
    border-radius: 11px;
    background: #fff;
    padding: 9px 10px;
    color: #172d4a;
    font-size: 12px;
    outline: none;
}

.beta-page-settings-dialog__field input:focus,
.beta-page-settings-dialog__field select:focus,
.beta-page-settings-dialog__field textarea:focus {
    border-color: var(--main-color);
    outline: 3px solid color-mix(in srgb, var(--main-color) 9%, transparent);
    outline-offset: 1px;
}

.beta-page-settings-dialog__toggles {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 7px;
    margin-top: 10px;
}

.beta-page-settings-dialog__toggles label {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    gap: 7px;
    align-items: center;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    background: #fff;
    padding: 8px 9px;
    cursor: pointer;
}

.beta-page-settings-dialog__toggles label.is-disabled {
    cursor: not-allowed;
    opacity: 0.55;
}

.beta-page-settings-dialog__toggles input {
    accent-color: var(--main-color);
}

.beta-page-settings-dialog__toggles strong {
    color: #334b68;
    font-size: 11px;
    font-weight: 700;
}

.beta-page-settings-dialog__images,
.beta-page-settings-dialog__columns {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
    margin-top: 9px;
}

.beta-page-settings-dialog__columns .beta-page-settings-dialog__field {
    margin-top: 0;
}

.beta-page-settings-dialog__advanced-fields {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
    margin-top: 9px;
    border: 0;
    border-radius: 0;
    background: transparent;
    padding: 0;
}

.beta-page-settings-dialog__footer {
    justify-content: flex-end;
    border-top: 1px solid #e8edf3;
    background: #fbfcfe;
}

.beta-page-settings-dialog__footer button {
    min-height: 38px;
    border-radius: var(--beta-button-radius);
    padding: 0 15px;
    font-size: 12px;
    font-weight: 700;
}

.beta-page-settings-dialog__footer .is-cancel {
    color: #687b94;
}

.beta-page-settings-dialog__footer .is-save {
    background: var(--main-color);
    color: #fff;
}

.beta-page-settings-dialog button:disabled {
    cursor: wait;
    opacity: 0.55;
}

@media (max-width: 700px) {
    .beta-page-settings-dialog {
        place-items: end center;
        padding: 0;
    }

    .beta-page-settings-dialog__panel {
        height: 94vh;
        border-radius: 24px 24px 0 0;
    }

    .beta-page-settings-dialog__header {
        flex-direction: column;
        align-items: start;
        padding: 13px 15px;
    }

    .beta-page-settings-dialog__header-actions {
        width: 100%;
        justify-content: flex-end;
    }

    .beta-page-settings-dialog__body {
        padding: 10px 14px 12px;
    }

    .beta-page-settings-dialog__toggles,
    .beta-page-settings-dialog__images,
    .beta-page-settings-dialog__columns,
    .beta-page-settings-dialog__advanced-fields {
        grid-template-columns: 1fr;
    }

    .beta-page-settings-dialog__footer {
        padding: 11px 14px;
    }
}
</style>
